import { isCoarsePointerDevice } from '@/platform/viewport/layoutViewport'

const DOUBLE_TAP_MS = 300

const ALLOW_DOUBLE_TAP_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  '[contenteditable="true"]',
  '[data-touch-dblclick]',
].join(', ')

/**
 * 是否允许触发双击/连点（资源选择、变量列表等）；其余区域拦截系统双击缩放。
 */
function shouldAllowDoubleTap(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return !!target.closest(ALLOW_DOUBLE_TAP_SELECTOR)
}

/**
 * 查找触摸点下可纵向滚动的最近祖先，用于区分列表内滚动与页面级下拉刷新。
 */
function findScrollableAncestor(element: Element | null): HTMLElement {
  let node = element
  while (node && node !== document.documentElement) {
    if (!(node instanceof HTMLElement)) break
    const style = window.getComputedStyle(node)
    const overflowY = style.overflowY
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  const root = document.scrollingElement
  return root instanceof HTMLElement ? root : document.documentElement
}

/**
 * 移动端禁止浏览器下拉刷新：顶部下拉且当前滚动容器已在顶时拦截 touchmove。
 */
function installTouchPullToRefreshGuard(): void {
  if (!isCoarsePointerDevice()) return

  let touchStartY = 0
  let pullGuardActive = false

  document.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) {
      pullGuardActive = false
      return
    }
    touchStartY = event.touches[0].clientY
    pullGuardActive = true
  }, { passive: true })

  document.addEventListener('touchmove', (event) => {
    if (!pullGuardActive || event.touches.length !== 1) return
    const deltaY = event.touches[0].clientY - touchStartY
    if (deltaY <= 0) return
    const target = event.target instanceof Element ? event.target : null
    const scrollable = findScrollableAncestor(target)
    if (scrollable.scrollTop > 0) return
    event.preventDefault()
  }, { passive: false })
}

/**
 * 安装触屏缩放防护，避免 iOS / 平板 / 移动端浏览器双击空白或捏合导致页面缩放错位；
 * 同时禁止移动端浏览器下拉刷新。
 */
export function installTouchZoomGuards(): void {
  if (typeof document === 'undefined') return

  installTouchPullToRefreshGuard()

  const blockGesture = (event: Event) => {
    event.preventDefault()
  }

  document.addEventListener('gesturestart', blockGesture, { passive: false })
  document.addEventListener('gesturechange', blockGesture, { passive: false })
  document.addEventListener('gestureend', blockGesture, { passive: false })

  let lastTouchEndAt = 0
  document.addEventListener('touchend', (event) => {
    if (event.touches.length > 0) return
    const now = Date.now()
    if (shouldAllowDoubleTap(event.target)) {
      lastTouchEndAt = now
      return
    }
    if (now - lastTouchEndAt <= DOUBLE_TAP_MS) {
      event.preventDefault()
    }
    lastTouchEndAt = now
  }, { passive: false })

  document.addEventListener('dblclick', (event) => {
    if (shouldAllowDoubleTap(event.target)) return
    event.preventDefault()
  })

  document.addEventListener('wheel', (event) => {
    if (!event.ctrlKey) return
    event.preventDefault()
  }, { passive: false })
}
